import { Color as PersonaColor } from '@persona-id/design-tokens';
import Color from 'color';
import styled, { css } from 'styled-components';

import { VersionDiffChangeType } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/types';
import { Z_INDICES } from 'dashboard/styles/variables';

export const FlowEditorHighlightColorVariant: Record<VersionDiffChangeType, PersonaColor> = {
  [VersionDiffChangeType.ADDED]: PersonaColor.BackgroundLabelGreenHover,
  [VersionDiffChangeType.MODIFIED]: PersonaColor.BackgroundLabelYellowHover,
  [VersionDiffChangeType.REMOVED]: PersonaColor.BackgroundLabelRedHover,
};

const NodeHighlighter = styled.div<{
  changeType: VersionDiffChangeType;
  height: number;
  isStep?: boolean;
}>`
  width: 100%;
  position: absolute;
  z-index: ${Z_INDICES.tooltip};
  ${({ height }) => {
    return css`
      min-height: ${height}px;
    `;
  }}
  ${({ changeType, isStep, theme }) => {
    const highlightColor = Color(FlowEditorHighlightColorVariant[changeType]).rgb();
    const highlightColorRgb = `${highlightColor.red()}, ${highlightColor.green()}, ${highlightColor.blue()}`;
    return css`
      background-color: ${`rgba(${highlightColorRgb}, 0.2)`};
      outline: ${`rgba(${highlightColorRgb}, 0.8)`} solid ${isStep ? `10px` : `5px`};
      ${isStep ? `` : `border-radius: ${theme.card.borderRadius};`}
    `;
  }}
`;

enum TransitionElement {
  CurrentStep = 'current_step',
  PreviousStep = 'previous_step',
}

const FlowEditorTransitionNodeHighlighterColorVariant: Record<
  TransitionElement,
  PersonaColor | null
> = {
  [TransitionElement.CurrentStep]: PersonaColor.CoreBlue700,
  [TransitionElement.PreviousStep]: null,
};

const FlowEditorTransitionNodeHighlighter = styled.div<{
  transitionElement: TransitionElement;
  height: number;
}>`
  width: 100%;
  position: absolute;
  z-index: ${Z_INDICES.tooltip};
  ${({ height }) => {
    return css`
      min-height: ${height}px;
    `;
  }}
  ${({ transitionElement }) => {
    const highlightColor = FlowEditorTransitionNodeHighlighterColorVariant[transitionElement];
    if (highlightColor) {
      return css`
        outline: solid 8px ${highlightColor};
      `;
    } else {
      return css`
        outline: unset;
      `;
    }
  }}
`;

interface FlowEditorNodeHighlighterProps {
  changeType?: VersionDiffChangeType;
  height: number;
  isStep?: boolean;
}

const FlowEditorNodeHighlighter = ({
  changeType,
  height,
  isStep,
}: FlowEditorNodeHighlighterProps) =>
  changeType != null ? (
    <NodeHighlighter changeType={changeType} height={height} isStep={isStep} />
  ) : (
    <></>
  );

export { FlowEditorNodeHighlighter, FlowEditorTransitionNodeHighlighter, TransitionElement };
